/**
 * Florida Municipal Permitting Contacts
 * ─────────────────────────────────────────────────────────────────────
 * Public-record main lines for permit/building departments in the
 * Tampa–Fort Myers–Lakeland service region. Used by the Permitting
 * Portal to give coordinators a one-click call/email out when a permit
 * stalls.
 *
 * Numbers are general department lines (not personal extensions).
 * Verify before any escalation that requires a specific inspector.
 *
 * IDENTITY: each municipality has a stable canonical `id` (e.g.
 * "hillsborough-county"). All cross-entity references (interaction logs,
 * future requirement profiles) MUST key off `id`, never the display name.
 */

/**
 * Canonical municipality identifier. `"unmapped"` is the fallback when
 * a job's permit municipality string can't be resolved to a known record
 * — coordination (interaction logging) must remain unblocked, but
 * municipality-specific logic (Phase 1B requirements) falls back to
 * the generic profile.
 */
export type MunicipalityId =
  | "unmapped"
  | "hillsborough-county"
  | "city-of-tampa"
  | "pinellas-county"
  | "city-of-st-petersburg"
  | "city-of-clearwater"
  | "manatee-county"
  | "sarasota-county"
  | "city-of-sarasota"
  | "polk-county"
  | "city-of-lakeland"
  | "lee-county"
  | "city-of-cape-coral"
  | "city-of-fort-myers"
  | "charlotte-county"
  | "pasco-county"
  | "hernando-county";

export interface MunicipalityContact {
  /** Canonical stable identifier — never derive from display name at use sites. */
  id: MunicipalityId;
  name: string;
  phone: string;          // E.164-friendly, e.g. "+18132725600"
  phoneDisplay: string;   // human "(813) 272-5600"
  email: string;
  website?: string;
}

const MUNICIPALITIES: Record<string, MunicipalityContact> = {
  "hillsborough county": {
    id: "hillsborough-county",
    name: "Hillsborough County Building Services",
    phone: "+18132725600",
    phoneDisplay: "(813) 272-5600",
    email: "DSDInfo@HCFL.gov",
    website: "https://hcfl.gov/development-services",
  },
  "city of tampa": {
    id: "city-of-tampa",
    name: "City of Tampa Construction Services",
    phone: "+18132743100",
    phoneDisplay: "(813) 274-3100",
    email: "ConstructionServicesCenter@tampagov.net",
  },
  "pinellas county": {
    id: "pinellas-county",
    name: "Pinellas County Building Services",
    phone: "+17274643888",
    phoneDisplay: "(727) 464-3888",
    email: "BldDept@pinellascounty.org",
  },
  "city of st. petersburg": {
    id: "city-of-st-petersburg",
    name: "St. Petersburg Construction Services",
    phone: "+17278937231",
    phoneDisplay: "(727) 893-7231",
    email: "construction.services@stpete.org",
  },
  "city of clearwater": {
    id: "city-of-clearwater",
    name: "Clearwater Planning & Development",
    phone: "+17275624567",
    phoneDisplay: "(727) 562-4567",
    email: "planning@myclearwater.com",
  },
  "manatee county": {
    id: "manatee-county",
    name: "Manatee County Building & Development",
    phone: "+19417493012",
    phoneDisplay: "(941) 749-3012",
    email: "building.services@mymanatee.org",
  },
  "sarasota county": {
    id: "sarasota-county",
    name: "Sarasota County Planning & Development",
    phone: "+19418616678",
    phoneDisplay: "(941) 861-6678",
    email: "permits@scgov.net",
  },
  "city of sarasota": {
    id: "city-of-sarasota",
    name: "City of Sarasota Building",
    phone: "+19419547260",
    phoneDisplay: "(941) 954-7260",
    email: "building@sarasotafl.gov",
  },
  "polk county": {
    id: "polk-county",
    name: "Polk County Building Division",
    phone: "+18635346080",
    phoneDisplay: "(863) 534-6080",
    email: "buildinginfo@polk-county.net",
  },
  "city of lakeland": {
    id: "city-of-lakeland",
    name: "Lakeland Building Inspection",
    phone: "+18638346012",
    phoneDisplay: "(863) 834-6012",
    email: "building@lakelandgov.net",
  },
  "lee county": {
    id: "lee-county",
    name: "Lee County Community Development",
    phone: "+12395338329",
    phoneDisplay: "(239) 533-8329",
    email: "DCDInfo@leegov.com",
  },
  "city of cape coral": {
    id: "city-of-cape-coral",
    name: "Cape Coral Community Development",
    phone: "+12395743157",
    phoneDisplay: "(239) 574-3157",
    email: "permits@capecoral.gov",
  },
  "city of fort myers": {
    id: "city-of-fort-myers",
    name: "Fort Myers Community Development",
    phone: "+12393213000",
    phoneDisplay: "(239) 321-3000",
    email: "permitting@cityftmyers.com",
  },
  "charlotte county": {
    id: "charlotte-county",
    name: "Charlotte County Community Development",
    phone: "+19417431201",
    phoneDisplay: "(941) 743-1201",
    email: "permits@charlottecountyfl.gov",
  },
  "pasco county": {
    id: "pasco-county",
    name: "Pasco County Building Construction Services",
    phone: "+17277744000",
    phoneDisplay: "(727) 774-4000",
    email: "BCS@pascocountyfl.net",
  },
  "hernando county": {
    id: "hernando-county",
    name: "Hernando County Building Division",
    phone: "+13525407500",
    phoneDisplay: "(352) 540-7500",
    email: "building@co.hernando.fl.us",
  },
};

/**
 * Resolve a municipality string (e.g. "Sarasota County", "Tampa") to its
 * permit-office contact. Tolerant of casing, leading "City of", and the
 * common "County" suffix omission.
 */
export function getMunicipalityContact(input?: string | null): MunicipalityContact | null {
  if (!input) return null;
  const norm = input.trim().toLowerCase();
  if (MUNICIPALITIES[norm]) return MUNICIPALITIES[norm];
  if (MUNICIPALITIES[`city of ${norm}`]) return MUNICIPALITIES[`city of ${norm}`];
  if (MUNICIPALITIES[`${norm} county`]) return MUNICIPALITIES[`${norm} county`];
  const stripped = norm.replace(/^city of\s+/, "");
  if (MUNICIPALITIES[stripped]) return MUNICIPALITIES[stripped];
  if (MUNICIPALITIES[`${stripped} county`]) return MUNICIPALITIES[`${stripped} county`];
  return null;
}

/**
 * Resolve a municipality display string to its canonical `MunicipalityId`.
 * Returns `null` when no match is found — callers MUST handle this branch
 * (do NOT fall back to slugifying the raw input; that would create
 * orphaned IDs that drift from the canonical roster).
 */
export function getMunicipalityId(input?: string | null): MunicipalityId | null {
  return getMunicipalityContact(input)?.id ?? null;
}

/** Resolve canonical id back to its contact record. */
export function getMunicipalityById(id: MunicipalityId): MunicipalityContact | null {
  for (const m of Object.values(MUNICIPALITIES)) {
    if (m.id === id) return m;
  }
  return null;
}

/** All canonical municipalities, ordered by display name. */
export function listMunicipalities(): MunicipalityContact[] {
  return Object.values(MUNICIPALITIES).sort((a, b) => a.name.localeCompare(b.name));
}
